import React, { useEffect, useMemo, useState } from 'react';
import { usePortal } from '../../contexts/PortalContext';
import { Gallery } from './Gallery';
import { VideoTranscript } from './VideoTranscript';
import { canDownload, isReviewable, mediaLink } from '../../utils/deliverables';
import { CHANGE_PROMPT, canRequestChanges } from '../../utils/changePrompt';
import { driveDownloadUrl, extractDriveFileId, toDrivePreviewUrl } from '../../utils/drive';
import { shareableItemLink, slugFromPath } from '../../utils/links';
import type { Deliverable } from '../../types/portal';

/**
 * BLOCK 2, WHEN THERE IS WORK ON SCREEN. The one thing that matters today:
 * the cut, its two buttons, and the transcript beside it.
 *
 * Lifted out of pages/client/Overview.tsx on 8 Oct 2026 unchanged in
 * behaviour — every rule below was learnt the hard way and is kept
 * deliberately:
 *
 * THE FOCUSED SLOT IS POLYMORPHIC. A film gets the player, photography
 * gets a real gallery with its name ABOVE the contact sheet, and an audio
 * master gets Drive's audio player rather than a video frame. `mediaLink`
 * exists because an Audio deliverable's link lives in `audioUrl`, which
 * this page once did not read — Bloomberg's Audio Podcast 03 was uploaded
 * and could not be played on the page it was uploaded to.
 *
 * A CUT IN REVIEW IS REVIEWED, EVERYTHING ELSE IS RECEIVED. "Ask for a
 * change" and the transcript column appear only for a Video or Audio that
 * is actually open for review. A podcast still being edited used to show a
 * black frame reading "Not ready to play yet" AND an empty transcript
 * column reading "No transcript on this one yet", which is two pieces of
 * bad news about something that is simply not finished.
 *
 * AND THIS IS THE ONLY PLACE A CHANGE IS ASKED FOR. The transcript page is
 * read-only. Tom: *"it should be one or the other surely?"*
 *
 * A PODCAST IN REVIEW GETS THE FILE. See `audioDownload`. Twenty-five
 * minutes of speech streamed as pixels through a locked-down corporate
 * browser is not a review, and no player we could build would change that.
 * What the page could do, and did not, was hand them the file.
 */

/**
 * WHICH TRANSCRIPT BELONGS TO THIS CUT. The transcript states what it is
 * for (`transcriptFor`, set in the studio). Before that it was inferred
 * from the two TITLES, which worked only when a transcript happened to be
 * named after its source: "Transcript 03" against "Audio Podcast 03"
 * matched nothing, and because transcripts are never tiles of their own,
 * 5,328 words sat in the database that no client and no member of staff
 * could see. The title match is kept as a fallback for transcripts
 * uploaded before the field existed, and only for those.
 */
function transcriptFor(all: Deliverable[], focus: Deliverable | undefined): Deliverable | undefined {
  if (!focus) return undefined;
  const transcripts = all.filter(item => item.type === 'Transcript');
  return transcripts.find(item => (item.transcriptFor ?? '') === focus.id) ?? transcripts.find(item => !(item.transcriptFor ?? '').trim() && item.name.includes(focus.name));
}
export function LeadCut({
  focus,
  all,
  projectComplete
}: {
  focus: Deliverable;
  all: Deliverable[];
  projectComplete: boolean;
}) {
  const {
    activeProject,
    openDeliverable,
    approveDeliverable,
    requestChanges,
    canApprove,
    canComment,
    notify
  } = usePortal();
  const [note, setNote] = useState('');
  const [timecode, setTimecode] = useState('');
  const [composing, setComposing] = useState(false);

  /* A new cut in the slot must never inherit the last one's half-written
     note — that note would be sent against the wrong film. */
  useEffect(() => {
    setNote('');
    setTimecode('');
    setComposing(false);
  }, [focus.id]);
  const isPhotos = focus.type === 'Photography';
  const isAudio = focus.type === 'Audio';
  /** A cut can be amended. A folder of frames, a document or a graphic is
      received, not reviewed line by line — see utils/changePrompt.ts. */
  const canChange = canRequestChanges(focus.type);
  /** With the client, now: ready to look at, or already being changed. */
  const inReview = isReviewable(focus.status) || focus.status === 'Changes Requested';
  const settled = focus.status === 'Approved' || focus.status === 'Delivered';
  const audioDownload = isAudio && !projectComplete && !canDownload(focus.status) ? driveDownloadUrl(extractDriveFileId(mediaLink(focus))) : '';
  const transcriptItem = useMemo(() => transcriptFor(all, focus), [all, focus]);
  const transcriptText = transcriptItem?.transcript || focus.transcript || '';
  const transcriptKey = transcriptItem?.id ?? focus.id;
  const withTranscript = !projectComplete && canChange && inReview;
  const changes = focus.changeRequests.filter(request => request.version === focus.version);
  const embed = isPhotos ? '' : toDrivePreviewUrl(mediaLink(focus));
  const onProjectLink = typeof window !== 'undefined' && Boolean(slugFromPath(window.location.pathname));
  const vertical = !isPhotos && !isAudio && focus.videoFormat === '9:16 Vertical';
  /* Audio gets Drive's own player, which is short and wide. The height is
     fixed and generous: the iframe is cross-origin so its content cannot be
     measured, Drive renders two different players, and too short crops the
     taller one at BOTH ends. */
  const frame = isAudio ? 'relative h-44 w-full overflow-hidden border border-rule bg-paperDeep' : `relative w-full overflow-hidden bg-ink ${vertical ? 'aspect-[9/16]' : 'aspect-video'}`;
  const quiet = 'label border border-rule px-5 py-3.5 text-graphite no-underline transition-colors duration-150 ease-out hover:border-ink hover:text-ink';
  async function copyLink() {
    const link = shareableItemLink(focus.id);
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      notify('Link copied — it opens on this film');
    } catch {
      notify('Could not copy — the address bar has the link');
    }
  }
  function send(event: React.FormEvent) {
    event.preventDefault();
    const body = note.trim();
    if (!body) return;
    requestChanges(focus.id, body, timecode);
    setNote('');
    setTimecode('');
    notify('Sent to Portal');
  }

  /**
   * A timecode clicked in the transcript opens the change box already
   * knowing where in the film the client means. The alternative was the
   * client describing the moment in words and somebody in the studio
   * scrubbing to find it.
   */
  function applyTimecode(label: string) {
    setTimecode(label);
    setComposing(true);
    notify(`${label} — say what needs to change`);
  }

  /* Written once and placed by both branches below, so a film and a gallery
     can never drift into offering different things. */
  const actions = <div className="flex flex-wrap items-center gap-3" data-id="element-70" data-mp="1h2zapxkwe0mb">
      {onProjectLink && <button type="button" onClick={copyLink} className={quiet} data-id="element-71" data-mp="1c5qs5vvu7ukj">
          Copy link
        </button>}
      {audioDownload && <a href={audioDownload} target="_blank" rel="noopener noreferrer" className={quiet} data-id="element-72" data-mp="1jn5i6f8q097m">
          Download to listen
        </a>}
      {inReview && !settled && canComment && canChange && <button type="button" onClick={() => setComposing(open => !open)} className={quiet} data-id="element-73" data-mp="1aplgx6auycx0">
          Ask for a change
        </button>}
      {inReview && !settled && canApprove && <button type="button" onClick={() => {
      approveDeliverable(focus.id);
      notify('Approved · Portal has been told');
    }} className="label bg-ink px-6 py-3.5 text-paper transition-opacity duration-150 ease-out hover:opacity-80" data-id="element-74" data-mp="22fq57g1qnk3i">
          Happy with this
        </button>}
    </div>;
  const composer = composing && canComment && canChange ? <form onSubmit={send} className="mt-10 border-t border-rule pt-8" data-id="element-75" data-mp="2q4iuwurrovpo">
        <label htmlFor="note" className="label block text-mute" data-id="element-76" data-mp="2q6n7s27n9523">
          What needs to change
        </label>
        {timecode && <p className="mt-4 flex flex-wrap items-center gap-4" data-id="element-77" data-mp="1wmmr4gdbuln0">
            <span className="label tabular-nums text-ink" data-id="element-78" data-mp="1wl3b8qvl2clr">At {timecode}</span>
            <button type="button" onClick={() => setTimecode('')} className="label-sm text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-79" data-mp="1rol6hr096e9f">
              Remove
            </button>
          </p>}
        <textarea id="note" rows={3} value={note} onChange={event => setNote(event.target.value)} placeholder={CHANGE_PROMPT} className="mt-4 w-full resize-none border-0 border-b border-ink bg-transparent pb-3 text-[1.05rem] leading-relaxed text-ink placeholder:text-mute focus:outline-none" data-id="element-80" data-mp="2fde55v89f07q" />
        <button type="submit" disabled={!note.trim()} className="label mt-5 bg-ink px-6 py-3.5 text-paper disabled:opacity-30" data-id="element-81" data-mp="u0b6fhk120ky">
          Send to Portal
        </button>
      </form> : null;
  const changeList = changes.length > 0 ? <ul className="mt-10 m-0 list-none border-t border-rule p-0" data-id="element-82" data-mp="3akoiek301r5v">
        {changes.map(change => <li key={change.id} className="flex gap-6 border-b border-rule py-4" data-id="element-83" data-mp="3fk1594tvey8g">
            <span className="label w-20 shrink-0 pt-1 tabular-nums text-mute" data-id="element-84" data-mp="3kdvoy89h7d7j">
              {change.timecode || '—'}
            </span>
            <span className="flex-1 whitespace-pre-line text-[0.98rem] leading-relaxed" data-id="element-85" data-mp="3tcz9lgoak0o5">
              {change.body}
            </span>
          </li>)}
      </ul> : null;

  /* The name leads, then the sheet. A caption under ninety-seven frames is
     a caption nobody reaches. */
  if (isPhotos) {
    return <div className="mt-12" data-id="element-86" data-mp="23zckci9zqvo7">
        <p className="label m-0 text-mute" data-id="element-87" data-mp="2405iiuuvwcuq">Showing now</p>
        <p className="mt-4 m-0 font-display text-3xl leading-none sm:text-4xl" data-id="element-88" data-mp="28wm69ny68p7a">{focus.name}</p>
        <div className="mt-9" data-id="element-89" data-mp="3prmg2p492mju">
          <Gallery deliverableKey={focus.id} data-id="element-90" data-mp="3psnp6o1pgyyx" />
        </div>
        <div className="mt-9" data-id="element-91" data-mp="3upnugwqqdxoz">{actions}</div>
        {composer}
        {changeList}
      </div>;
  }
  return <div className={`mt-12 ${withTranscript ? 'flex flex-col gap-10 lg:flex-row' : ''}`} data-id="element-92" data-mp="3jmov3pjbwaq">
      <div className={withTranscript ? 'lg:w-[62%]' : ''} data-id="element-93" data-mp="7lq668yvxv3x">
        {/* A vertical film is narrow by nature, so its details sit beside it
            rather than leaving a column of empty paper. */}
        <div className={vertical ? 'flex flex-col gap-10 sm:flex-row sm:items-start' : ''} data-id="element-94" data-mp="hivwhezodi33">
          <div className={vertical ? 'w-full sm:w-[320px] sm:shrink-0' : 'w-full'} data-id="element-95" data-mp="m9ugaojvgyb3">
            <div className={frame} data-id="element-96" data-mp="r97cw10jpjqv">
              {embed ? <iframe key={focus.id} src={embed} title={focus.name} allow={isAudio ? 'autoplay' : 'autoplay; fullscreen'} allowFullScreen={!isAudio} className="h-full w-full border-0" data-id="element-97" data-mp="r8nr297ryb7k" /> : <div className="flex h-full w-full items-center justify-center" data-id="element-98" data-mp="2ft36l8b3rbzl">
                  <p className={`label ${isAudio ? 'text-mute' : 'text-paper/45'}`} data-id="element-99" data-mp="2fvfvk5fa2mm5">
                    {isAudio ? 'Not ready to play yet' : 'Not ready to view yet'}
                  </p>
                </div>}
            </div>
          </div>

          <div className={vertical ? 'flex-1' : 'mt-7'} data-id="element-100" data-mp="33hdy3kuwrzb7">
            <p className="label m-0 text-mute" data-id="element-101" data-mp="2zhc6k4iw0m9n">Playing now</p>
            <p className="mt-4 m-0 font-display text-3xl leading-none sm:text-4xl" data-id="element-102" data-mp="2zhvohl8jfano">{focus.name}</p>
            <div className="mt-7" data-id="element-103" data-mp="2uoj2zjz5njz5">{actions}</div>
            {audioDownload && <p className="label-sm mt-4 m-0 text-mute" data-id="element-104" data-mp="2pqhjwz1qbkno">
                Best downloaded if you want to listen right through
              </p>}
          </div>
        </div>

        {composer}
        {changeList}
      </div>

      {withTranscript && <div className="lg:w-[38%] lg:border-l lg:border-rule lg:pl-10" data-id="element-105" data-mp="56aozrer2k5r">
          <div className="flex items-baseline justify-between" data-id="element-106" data-mp="a5vyuwvgkcx9">
            <p className="label text-mute" data-id="element-107" data-mp="a5dvzheaj0gd">Transcript</p>
            {transcriptItem && <button type="button" onClick={() => openDeliverable(transcriptItem.id)} className="label text-graphite hover:text-ink" data-id="element-108" data-mp="3sj3g82z8c4li">
                Read in full
              </button>}
          </div>
          <VideoTranscript projectSlug={activeProject.slug} deliverableKey={transcriptKey} fallback={transcriptText || 'No transcript on this one yet.'} onUseTimecode={inReview && !settled && canComment ? applyTimecode : undefined} compact className="mt-5 max-h-[26rem] overflow-y-auto" data-id="element-109" data-mp="3nl1ybldjibbm" />
        </div>}
    </div>;
}